/**
 * ComisionesView — cálculo automático de bonos trimestrales por comercial.
 *
 * Reglas de bono (definidas por el negocio):
 *  - Facturación AR trimestral ≥ umbral (default $140.000.000)  → 90% del bono
 *  - % del roster usado ≥ umbral (default 70%)                  → 10% del bono
 *  - Ambos                                                      → 100% del bono
 *  - El bono se PAGA sólo si % de cobro ≥ umbral (default 80%).
 *    Si no llega, se muestra "PENDIENTE COBRO" con el monto teórico.
 *  - Monto = sueldo del último mes del trimestre × bono %.
 *
 * Los umbrales son editables y se guardan en localStorage (no requiere backend).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Download, RefreshCw, Settings2, Eye, FileSpreadsheet, FileText, Search, Gift, ListChecks, CheckCircle2, XCircle } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from './ui/accordion';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ComisionesWarnings } from './ComisionesWarnings';

const LS_THRESHOLDS_KEY = 'comisiones_thresholds_v2';
const DEFAULT_QUARTER_TH = {
  facturacion_min: 140_000_000,
  roster_pct_min: 75,
  cobro_pct_min: 80,
};
const DEFAULT_THRESHOLDS = {
  Q1: { ...DEFAULT_QUARTER_TH },
  Q2: { ...DEFAULT_QUARTER_TH },
  Q3: { ...DEFAULT_QUARTER_TH },
  Q4: { ...DEFAULT_QUARTER_TH },
};

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const QUARTER_LABEL = {
  Q1: 'Q1 · Ene · Feb · Mar',
  Q2: 'Q2 · Abr · May · Jun',
  Q3: 'Q3 · Jul · Ago · Sep',
  Q4: 'Q4 · Oct · Nov · Dic',
};

// Meses completos del trimestre (para headers institucionales en modal / PDF)
const QUARTER_FULL_MONTHS = {
  Q1: 'Enero · Febrero · Marzo',
  Q2: 'Abril · Mayo · Junio',
  Q3: 'Julio · Agosto · Septiembre',
  Q4: 'Octubre · Noviembre · Diciembre',
};

const fmtARS = (v) => {
  const n = Number(v) || 0;
  return '$' + n.toLocaleString('es-AR', { maximumFractionDigits: 0 });
};

// Tiered bono logic (definido por negocio 2026-08-29, actualizado 2026-09-03):
// El multiplicador se aplica al SUELDO MES 2 del trimestre.
//   < 85%   fact           → 0    (no cobra)
//   85-94%  fact           → 0.25 sueldo
//   95-99%  fact           → 0.50 sueldo
//   100-119% fact          → 1.00 sueldo
//   120-139% fact          → 1.25 sueldos
//   ≥140%   fact           → 1.50 sueldos (tope)
// Ajuste por objetivo estratégico (roster ≥ umbral):
//   - Cumplir el estratégico es un BONUS, no un requisito. El comercial que
//     llega a facturación pero no al roster COBRA IGUAL, con un 10% menos.
//   - Regla vieja "sub-100 sin estratégico → 0" fue eliminada por pedido del
//     usuario (2026-09-03): "no se puede cobrar el estratégico si no se cubre
//     facturación, pero sí viceversa".
//   - Si cumple estratégico                   → multiplicador × 1.00
//   - Si NO cumple estratégico                → multiplicador × 0.90
// El pago efectivo depende de cobro ≥ umbral (si no, queda "PENDIENTE COBRO").
const computeBono = (cumplPct, cumpleEstrat) => {
  let mult = 0;
  let tierLabel = '<85%';
  if (cumplPct >= 140) { mult = 1.50; tierLabel = '140%+'; }
  else if (cumplPct >= 120) { mult = 1.25; tierLabel = '120%'; }
  else if (cumplPct >= 100) { mult = 1.00; tierLabel = '100%'; }
  else if (cumplPct >= 95) { mult = 0.50; tierLabel = '95%'; }
  else if (cumplPct >= 85) { mult = 0.25; tierLabel = '85%'; }

  if (mult === 0) return { multiplier: 0, tierLabel, appliedPenalty: false };
  if (!cumpleEstrat) {
    return { multiplier: mult * 0.9, tierLabel, appliedPenalty: true };
  }
  return { multiplier: mult, tierLabel, appliedPenalty: false };
};

// Load / persist thresholds via localStorage.
// v2 stores per-quarter overrides: { Q1:{...}, Q2:{...}, Q3:{...}, Q4:{...} }.
// If a legacy v1 blob exists (single set for all quarters), we migrate it by
// applying the same values to all 4 quarters.
const loadThresholds = () => {
  try {
    const raw = localStorage.getItem(LS_THRESHOLDS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Merge each quarter with defaults so any missing key is filled.
      return {
        Q1: { ...DEFAULT_QUARTER_TH, ...(parsed.Q1 || {}) },
        Q2: { ...DEFAULT_QUARTER_TH, ...(parsed.Q2 || {}) },
        Q3: { ...DEFAULT_QUARTER_TH, ...(parsed.Q3 || {}) },
        Q4: { ...DEFAULT_QUARTER_TH, ...(parsed.Q4 || {}) },
      };
    }
    // Try legacy v1
    const legacy = localStorage.getItem('comisiones_thresholds_v1');
    if (legacy) {
      const p = JSON.parse(legacy);
      const single = { ...DEFAULT_QUARTER_TH, ...p };
      return { Q1: { ...single }, Q2: { ...single }, Q3: { ...single }, Q4: { ...single } };
    }
  } catch { /* ignore */ }
  return DEFAULT_THRESHOLDS;
};

const saveThresholds = (t) => {
  try { localStorage.setItem(LS_THRESHOLDS_KEY, JSON.stringify(t)); } catch { /* ignore */ }
};

// Green ✓ / red ✗ pill for boolean cumplimiento
const Check = ({ ok }) => (
  <span
    className="inline-flex items-center justify-center rounded-full font-bold text-[10px]"
    style={{
      width: 22, height: 22,
      backgroundColor: ok ? '#1B6B3A' : '#7f1d1d',
      color: 'white',
    }}
  >
    {ok ? '✓' : '✗'}
  </span>
);

const ComisionesView = () => {
  const [year, setYear] = useState(2026);
  const [quarter, setQuarter] = useState(() => {
    const m = new Date().getMonth() + 1;
    if (m <= 3) return 'Q1';
    if (m <= 6) return 'Q2';
    if (m <= 9) return 'Q3';
    return 'Q4';
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [thresholds, setThresholds] = useState(loadThresholds);
  // Modal de detalle de facturas por comercial × trimestre
  const [detailFor, setDetailFor] = useState(null); // { kam, invoices, fmtARSize, ... }
  const [detailSearch, setDetailSearch] = useState(''); // filtra facturas por nº

  // ---- Export helpers for the invoice detail modal ---------------------
  const buildDetailRows = useCallback((detail) => {
    if (!detail) return [];
    return [...detail.invoices]
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''))
      .map((inv) => ({
        Fecha: (inv.fecha || '').slice(0, 10),
        'Nº Factura': inv.nro_factura || '',
        'Comercial (raw)': inv.kam_raw || '',
        Marca: inv.marca || '',
        Agencia: inv.agencia || '',
        Influencer: inv.influencer || '',
        'Neto factura': Math.round(inv.bruto_total || 0),
        'Duración (m)': inv.duracion || 1,
        'Split %': Math.round((inv.split_weight || 0) * 100),
        'Meses en Q': inv.meses_en_quarter || 1,
        [`Atribuido ${quarter}`]: Math.round(inv.attributed || 0),
        Cobrado: inv.excluido_cobro ? ((inv.tipo_doc || '').startsWith('NC') ? 'NC (excl.)' : 'Anulada (excl.)') : (inv.cobrado ? 'SÍ' : 'NO'),
        'Monto cobrado': Math.round(inv.cobrado_attributed || 0),
      }));
  }, [quarter]);

  const exportDetailToExcel = useCallback((detail) => {
    const rows = buildDetailRows(detail);
    const totalAtribuido = rows.reduce((s, r) => s + (r[`Atribuido ${quarter}`] || 0), 0);
    const totalCobrado = rows.reduce((s, r) => s + (r['Monto cobrado'] || 0), 0);
    const withTotal = [
      ...rows,
      {},
      { Fecha: 'TOTALES', [`Atribuido ${quarter}`]: totalAtribuido, 'Monto cobrado': totalCobrado },
    ];
    const ws = XLSX.utils.json_to_sheet(withTotal);
    // Auto-size columns based on the longest string per column
    const headers = Object.keys(rows[0] || {});
    ws['!cols'] = headers.map((h) => ({
      wch: Math.max(h.length, ...withTotal.map((r) => String(r[h] ?? '').length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${detail.kam}-${quarter}`);
    XLSX.writeFile(wb, `facturas-${detail.kam}-${quarter}-${year}.xlsx`);
  }, [buildDetailRows, quarter, year]);

  const exportDetailToPDF = useCallback((detail) => {
    const rows = buildDetailRows(detail);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const fmt = (n) => new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    // ===== PALETA INSTITUCIONAL =====
    const BRAND = [27, 107, 58];        // Verde Talengers (mismo que header tabla)
    const INK = [20, 20, 20];           // Texto principal
    const MUTED = [110, 110, 110];      // Texto secundario
    const SOFT_BG = [245, 247, 245];    // Fondo paneles
    const DIVIDER = [220, 220, 220];
    const RED = [230, 57, 70];
    const AMBER = [244, 162, 97];
    const GREEN = [27, 107, 58];

    const QUARTER_MONTHS_LABEL = {
      Q1: 'Enero · Febrero · Marzo',
      Q2: 'Abril · Mayo · Junio',
      Q3: 'Julio · Agosto · Septiembre',
      Q4: 'Octubre · Noviembre · Diciembre',
    };

    const fullName = detail.sueldoFullName || detail.kam;
    const th = detail.thresholds || DEFAULT_QUARTER_TH;
    const fechaEmision = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });

    // ===== CARÁTULA / HEADER INSTITUCIONAL =====
    // Banda superior verde
    doc.setFillColor(...BRAND);
    doc.rect(0, 0, pageW, 70, 'F');

    // Logo/wordmark TALENGERS
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.setTextColor(255, 255, 255);
    doc.text('TALENGERS', 40, 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(220, 240, 225);
    doc.text('TALENT MANAGEMENT AGENCY', 40, 55);

    // Metadata derecha
    doc.setFontSize(8);
    doc.setTextColor(220, 240, 225);
    doc.text('INFORME DE COMISIONES', pageW - 40, 32, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`${quarter} ${year}`, pageW - 40, 48, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(220, 240, 225);
    doc.text(`Emitido: ${fechaEmision}`, pageW - 40, 61, { align: 'right' });

    // ===== CARÁTULA: Nombre del comercial + trimestre =====
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text('Documento preparado para', 40, 100);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(...INK);
    doc.text(fullName.toUpperCase(), 40, 128);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    doc.text(`Comercial · ${detail.kam} · Trimestre ${quarter} ${year} (${QUARTER_MONTHS_LABEL[quarter] || ''})`, 40, 145);

    // Línea divisoria
    doc.setDrawColor(...DIVIDER);
    doc.setLineWidth(0.5);
    doc.line(40, 160, pageW - 40, 160);

    // ===== FICHA DE CUMPLIMIENTO (KPI PANELS) =====
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text('FICHA DE CUMPLIMIENTO · RESUMEN DEL TRIMESTRE', 40, 180);

    // Grid de 4 KPIs principales
    const panelY = 195;
    const panelH = 80;
    const gap = 10;
    const panelW = (pageW - 80 - gap * 3) / 4;

    const drawKpi = (x, label, value, sub, valueColor) => {
      doc.setFillColor(...SOFT_BG);
      doc.roundedRect(x, panelY, panelW, panelH, 4, 4, 'F');
      // Borde sutil
      doc.setDrawColor(...DIVIDER);
      doc.setLineWidth(0.3);
      doc.roundedRect(x, panelY, panelW, panelH, 4, 4, 'S');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(...MUTED);
      doc.text(label.toUpperCase(), x + 10, panelY + 15);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(...(valueColor || INK));
      doc.text(value, x + 10, panelY + 40);

      if (sub) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...MUTED);
        // Soporta multi-línea via "\n" o array de strings
        const subStr = Array.isArray(sub) ? sub.join('\n') : sub;
        const subLines = subStr
          .split('\n')
          .flatMap((line) => doc.splitTextToSize(line, panelW - 20));
        doc.text(subLines, x + 10, panelY + 54, { lineHeightFactor: 1.35 });
      }
    };

    const cumplColor = detail.cumplPct >= 100 ? GREEN : (detail.cumplPct >= 85 ? AMBER : RED);
    const cobroColor = detail.cumpleCobro ? GREEN : RED;
    const rosterColor = detail.cumpleEstrat ? GREEN : AMBER;

    // Total del roster para el quarter (máx de los meses, o derivado de pct si no hay monthly)
    const rosterTotal = detail.roster_pct_monthly
      ? Math.max(0, ...Object.values(detail.roster_pct_monthly).map((mv) => mv.roster_total || 0))
      : ((detail.roster_pct || 0) > 0
          ? Math.round((detail.roster_used_count || 0) * 100 / detail.roster_pct)
          : 0);

    // Orden pedido por usuario: Facturación · Roster Estratégico · Bono · % Cobro (último)
    drawKpi(40,
      'Facturación AR',
      `$${fmt(detail.facturacion_bruto || 0)}`,
      `Objetivo de Facturación: $${fmt(th.facturacion_min || 0)}  ·  Logrado ${(detail.cumplPct || 0).toFixed(1)}%`,
      cumplColor
    );
    drawKpi(40 + (panelW + gap),
      'Roster Estratégico',
      `${(detail.roster_pct || 0).toFixed(1)}%`,
      `Umbral: ${th.roster_pct_min}%  ·  ${detail.roster_used_count || 0}/${rosterTotal} talentos contratados`,
      rosterColor
    );
    drawKpi(40 + (panelW + gap) * 2,
      'Bono del Trimestre',
      `$${fmt(detail.bonoMonto || 0)}`,
      `Tier ${detail.tierLabel || '—'}  ·  x ${(detail.multiplier || 0).toFixed(2)}${detail.appliedPenalty ? ' (-10% estrat.)' : ''}\nBase de cálculo: $${fmt(detail.sueldo_ultimo_mes || 0)} (sueldo mes 2)`,
      (detail.multiplier || 0) > 0 ? GREEN : MUTED
    );
    drawKpi(40 + (panelW + gap) * 3,
      '% Cobro',
      `${(detail.cobro_pct || 0).toFixed(1)}%`,
      `Umbral: ${th.cobro_pct_min}%  ·  ${fmt(detail.invoices.length)} facturas  ·  ${fmt((detail.invoices || []).filter((i) => i.cobrado).length)} cobradas`,
      cobroColor
    );

    // ===== STATUS DEL BONO · Barra ancha debajo de los KPIs =====
    const statusRowY = panelY + panelH + 18;
    const statusBarH = 46;
    const statusColor = detail.multiplier === 0 ? MUTED : (detail.cumpleCobro ? GREEN : AMBER);
    doc.setFillColor(...statusColor);
    doc.roundedRect(40, statusRowY, pageW - 80, statusBarH, 4, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text('STATUS DEL BONO', 54, statusRowY + 15);
    doc.setFontSize(18);
    doc.text(detail.statusText || '—', 54, statusRowY + 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(
      detail.multiplier === 0
        ? 'No alcanza umbral mínimo de facturación'
        : (detail.cumpleCobro ? 'Listo para pagar' : 'Pendiente llegar al umbral de cobro'),
      pageW - 54, statusRowY + 28, { align: 'right' }
    );

    // ===== TABLA DE DETALLE DE FACTURAS =====
    const tableStartY = statusRowY + statusBarH + 22;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text(`DETALLE DE FACTURAS ATRIBUIDAS AL ${quarter} ${year}`, 40, tableStartY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
      `${rows.length} facturas  ·  Split y duración ya aplicados sobre neto`,
      40, tableStartY + 13
    );

    const totalAtribuido = rows.reduce((s, r) => s + (r[`Atribuido ${quarter}`] || 0), 0);
    const totalCobrado = rows.reduce((s, r) => s + (r['Monto cobrado'] || 0), 0);

    autoTable(doc, {
      startY: tableStartY + 22,
      head: [['Fecha', 'Nº Fact', 'Marca', 'Agencia', 'Influencer', 'Neto', 'Dur.', 'Split', `Atribuido ${quarter}`, 'Cobrado']],
      body: rows.map((r) => [
        r.Fecha,
        r['Nº Factura'] || '—',
        r.Marca,
        r.Agencia,
        r.Influencer,
        `$${fmt(r['Neto factura'])}`,
        `${r['Duración (m)']}m`,
        `${r['Split %']}%${r['Meses en Q'] > 1 ? ` ×${r['Meses en Q']}m` : ''}`,
        `$${fmt(r[`Atribuido ${quarter}`])}`,
        r.Cobrado,
      ]),
      foot: [[
        '', '', '', '', 'TOTALES', '', '', '',
        `$${fmt(totalAtribuido)}`,
        `$${fmt(totalCobrado)}`,
      ]],
      styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: BRAND, textColor: 255, fontStyle: 'bold' },
      footStyles: { fillColor: [30, 30, 30], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        1: { fontStyle: 'bold' },
        5: { halign: 'right' },
        6: { halign: 'center' },
        7: { halign: 'center' },
        8: { halign: 'right' },
        9: { halign: 'center' },
      },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 40, right: 40, bottom: 50 },
      didDrawPage: () => {
        // ===== PIE DE PÁGINA INSTITUCIONAL =====
        const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
        const totalPages = doc.internal.getNumberOfPages();
        doc.setDrawColor(...DIVIDER);
        doc.setLineWidth(0.3);
        doc.line(40, pageH - 35, pageW - 40, pageH - 35);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...MUTED);
        doc.text('TALENGERS · Documento confidencial · Uso interno exclusivo del comercial destinatario', 40, pageH - 22);
        doc.text(`${fullName} · ${quarter} ${year}`, pageW / 2, pageH - 22, { align: 'center' });
        doc.text(`Página ${pageNum} de ${totalPages}`, pageW - 40, pageH - 22, { align: 'right' });
      },
    });

    const safeName = fullName.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    doc.save(`Comisiones-${safeName}-${quarter}-${year}.pdf`);
  }, [buildDetailRows, quarter, year]);

  useEffect(() => { saveThresholds(thresholds); }, [thresholds]);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${backend}/api/dashboard/comisiones?year=${year}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData(d);
    } catch (e) {
      setError(e.message || 'Error');
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Rows for the current quarter, hydrated with bono calculation
  const rows = useMemo(() => {
    if (!data) return [];
    const th = thresholds[quarter] || DEFAULT_QUARTER_TH;
    const perCom = data.quarters?.[quarter] || {};
    return Object.entries(perCom)
      .map(([kam, b]) => {
        // cumplimiento facturación = actual / target × 100
        const cumplPct = th.facturacion_min > 0 ? (b.facturacion_bruto / th.facturacion_min) * 100 : 0;
        const cumpleEstrat = b.roster_pct >= th.roster_pct_min;
        const cumpleCobro = b.cobro_pct >= th.cobro_pct_min;
        const { multiplier, tierLabel, appliedPenalty } = computeBono(cumplPct, cumpleEstrat);
        const bonoMonto = (b.sueldo_ultimo_mes || 0) * multiplier;
        return {
          kam,
          ...b,
          cumplPct,
          cumpleEstrat,
          cumpleCobro,
          tierLabel,
          multiplier,
          appliedPenalty,
          bonoPct: Math.round(multiplier * 100),
          bonoMonto,
          sueldoFullName: data.salary_names_by_short?.[kam] || null,
        };
      })
      // Sort: facturación desc so top performers first
      .sort((a, b) => b.facturacion_bruto - a.facturacion_bruto);
  }, [data, quarter, thresholds]);

  // Umbrales del trimestre activo (usados en headers y modal)
  const th = thresholds[quarter] || DEFAULT_QUARTER_TH;

  // Helper para actualizar un umbral de un trimestre puntual
  const setQuarterTh = (q, patch) =>
    setThresholds((prev) => ({ ...prev, [q]: { ...(prev[q] || DEFAULT_QUARTER_TH), ...patch } }));

  const exportCsv = () => {
    if (!data) return;
    const header = [
      'Comercial', 'Nombre completo', 'Facturación AR', 'Cobrado', '% Cobro', '% Cumplimiento',
      'Talentos usados', '% Roster', 'Sueldo mes 2', 'Cumple Cobro', 'Cumple Estratégico',
      'Tier', 'Multiplicador', 'Penalidad estratégico', 'Monto Bono', 'Status'
    ];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      const status = r.multiplier === 0
        ? 'SIN BONO'
        : (r.cumpleCobro ? 'A PAGAR' : 'PENDIENTE COBRO');
      lines.push([
        r.kam,
        r.sueldoFullName || '',
        r.facturacion_bruto.toFixed(0),
        r.cobrado_bruto.toFixed(0),
        r.cobro_pct.toFixed(1),
        r.cumplPct.toFixed(1),
        r.roster_used_count,
        r.roster_pct.toFixed(1),
        r.sueldo_ultimo_mes.toFixed(0),
        r.cumpleCobro ? 'SI' : 'NO',
        r.cumpleEstrat ? 'SI' : 'NO',
        r.tierLabel,
        r.multiplier.toFixed(2),
        r.appliedPenalty ? 'SI (-10%)' : 'NO',
        r.bonoMonto.toFixed(0),
        status,
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comisiones_${year}_${quarter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const _unusedNoop = null;  // eslint-disable-line no-unused-vars
  // <Check /> comes from module-level definition above.

  return (
    <div className="px-6 py-6 space-y-4 max-w-7xl mx-auto" data-testid="comisiones-view">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.06em' }}>
            Comisiones · Bono Trimestral
          </h1>
          <p className="text-xs text-gray-500 font-mono mt-1">
            Facturación AR + Cobro + Uso del roster · {rows.length} comerciales
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-gray-900 border border-gray-700 text-white font-semibold min-w-[110px]" data-testid="comisiones-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2026">2026</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
            </SelectContent>
          </Select>
          <Select value={quarter} onValueChange={setQuarter}>
            <SelectTrigger className="bg-gray-900 border border-gray-700 text-white font-semibold min-w-[200px]" data-testid="comisiones-quarter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUARTERS.map((q) => (<SelectItem key={q} value={q}>{QUARTER_LABEL[q]}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowRules((v) => !v)} className="bg-gray-900 border-gray-700 text-white" data-testid="comisiones-rules-toggle">
            <ListChecks size={14} className="mr-1.5" /> Reglas
          </Button>
          <Button variant="outline" onClick={() => setShowConfig((v) => !v)} className="bg-gray-900 border-gray-700 text-white" data-testid="comisiones-config-toggle">
            <Settings2 size={14} className="mr-1.5" /> Umbrales
          </Button>
          <Button variant="outline" onClick={fetchData} disabled={loading} className="bg-gray-900 border-gray-700 text-white" data-testid="comisiones-refresh">
            <RefreshCw size={14} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} />{loading ? 'Cargando...' : 'Actualizar'}
          </Button>
          <Button onClick={exportCsv} disabled={!data || rows.length === 0} className="text-black font-semibold" style={{ backgroundColor: '#c4b5fd' }} data-testid="comisiones-export">
            <Download size={14} className="mr-1.5" /> Exportar CSV
          </Button>
        </div>
      </div>

      <ComisionesWarnings warnings={data?.warnings} />

      {/* Panel de reglas de negocio — FC/NC inclusión-exclusión */}
      {showRules && (
        <Card className="border-violet-500/20" data-testid="comisiones-rules-panel" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks size={16} className="text-violet-300" />
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Reglas de Facturas (FC) y Notas de Crédito (NC)</h3>
            </div>
            <p className="text-xs text-gray-400 mb-3">
              Así se decide qué facturas y notas de crédito entran en el cálculo de comisiones de cada trimestre.
            </p>
            <Accordion type="single" collapsible className="w-full" data-testid="comisiones-rules-accordion">
              {[
                {
                  n: 1,
                  figura: false,
                  title: 'FC y NC en el mismo Q, y la NC anula totalmente la FC',
                  detail: 'Si la Nota de Crédito anula el 100% del monto de la Factura y ambas caen en el mismo trimestre, ninguna de las dos aparece en el reporte (se cancelan entre sí).',
                },
                {
                  n: 2,
                  figura: true,
                  title: 'FC y NC en distinto Q',
                  detail: 'Si la Factura y su Nota de Crédito pertenecen a trimestres distintos, ambas figuran: la FC se atribuye a su Q y la NC se atribuye (resta) en el Q en que fue emitida.',
                },
                {
                  n: 3,
                  figura: true,
                  title: 'La NC anula parcialmente la FC',
                  detail: 'Si el monto de la Nota de Crédito es menor al de la Factura (anulación parcial), ambas figuran en el reporte, con el neto resultante.',
                },
                {
                  n: 4,
                  figura: false,
                  title: 'FC o NC sin "Mes Pedido"',
                  detail: 'Cualquier Factura o Nota de Crédito que no tenga un valor cargado en la columna "Mes Pedido" queda excluida del reporte (aplica a partir de Q3 en adelante).',
                },
                {
                  n: 5,
                  figura: null,
                  title: 'Relación NC ↔ FC por número en columna "Detalle"',
                  detail: 'La Nota de Crédito se vincula a su Factura usando el número de comprobante que aparece en la columna "Detalle" de la NC. Si no hay ningún número en "Detalle", la NC queda sin vincular.',
                },
                {
                  n: 6,
                  figura: false,
                  title: 'Palabras "MARK" o "GASTO" en columna "Detalle"',
                  detail: 'Cualquier fila (FC o NC) cuya columna "Detalle" contenga las palabras "Mark", "gasto" o "gastos" queda excluida del reporte, sin importar el resto de las reglas.',
                },
                {
                  n: 7,
                  figura: null,
                  title: 'Doble chequeo: número de FC + Razón Social',
                  detail: 'El matcheo de una NC con su FC exige coincidencia simultánea de: (a) los últimos 4 dígitos del número de comprobante encontrados en "Detalle", y (b) la Razón Social exacta. El Influencer no se tiene en cuenta para este chequeo.',
                },
              ].map((r) => (
                <AccordionItem key={r.n} value={`rule-${r.n}`} className="border-neutral-800" data-testid={`comisiones-rule-item-${r.n}`}>
                  <AccordionTrigger className="text-gray-200 hover:no-underline hover:text-white py-3">
                    <div className="flex items-center gap-3 text-left">
                      <span
                        className="inline-flex items-center justify-center rounded-full font-bold text-[11px] w-6 h-6 shrink-0"
                        style={{ backgroundColor: '#1B6B3A22', color: '#4ADE80', border: '1px solid #1B6B3A55' }}
                      >
                        {r.n}
                      </span>
                      <span className="text-sm">{r.title}</span>
                      {r.figura === true && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0" style={{ backgroundColor: '#1B6B3A22', color: '#4ADE80', border: '1px solid #1B6B3A55' }}>
                          <CheckCircle2 size={10} /> Figura
                        </span>
                      )}
                      {r.figura === false && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0" style={{ backgroundColor: '#7f1d1d22', color: '#f87171', border: '1px solid #7f1d1d55' }}>
                          <XCircle size={10} /> No figura
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-xs text-gray-400 pl-9">
                    {r.detail}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Config panel — un set de umbrales POR TRIMESTRE */}
      {showConfig && (
        <Card className="border-violet-500/20" style={{ backgroundColor: '#111' }}>
          <CardContent className="p-4">
            <p className="text-xs text-gray-400 mb-3">
              Cada trimestre tiene sus propios umbrales. Los cambios se guardan en tu navegador (no afecta a otros usuarios).
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="comisiones-th-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 border-b border-neutral-800">
                    <th className="px-2 py-2 text-left w-24">Trimestre</th>
                    <th className="px-2 py-2 text-left">Facturación mín (ARS)</th>
                    <th className="px-2 py-2 text-left w-32">% Roster mín</th>
                    <th className="px-2 py-2 text-left w-32">% Cobro mín</th>
                  </tr>
                </thead>
                <tbody>
                  {QUARTERS.map((q) => {
                    const qth = thresholds[q] || DEFAULT_QUARTER_TH;
                    const isActive = q === quarter;
                    return (
                      <tr key={q} className="border-b border-neutral-900" style={isActive ? { backgroundColor: '#4c1d9522' } : {}}>
                        <td className="px-2 py-2 font-bold" style={{ color: isActive ? '#c4b5fd' : '#e5e7eb' }}>
                          {q}{isActive && <span className="ml-1 text-[9px] text-violet-400">activo</span>}
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            value={qth.facturacion_min}
                            onChange={(e) => setQuarterTh(q, { facturacion_min: Number(e.target.value) || 0 })}
                            className="bg-gray-800 border-gray-700 text-white h-8 text-xs"
                            data-testid={`comisiones-th-${q}-fact`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.5"
                            value={qth.roster_pct_min}
                            onChange={(e) => setQuarterTh(q, { roster_pct_min: Number(e.target.value) || 0 })}
                            className="bg-gray-800 border-gray-700 text-white h-8 text-xs"
                            data-testid={`comisiones-th-${q}-roster`}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.5"
                            value={qth.cobro_pct_min}
                            onChange={(e) => setQuarterTh(q, { cobro_pct_min: Number(e.target.value) || 0 })}
                            className="bg-gray-800 border-gray-700 text-white h-8 text-xs"
                            data-testid={`comisiones-th-${q}-cobro`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setThresholds(DEFAULT_THRESHOLDS)}
                className="text-xs text-violet-300 hover:underline"
                data-testid="comisiones-th-reset"
              >
                Restaurar todos por defecto ($140M · 75% · 80%)
              </button>
              <button
                onClick={() => {
                  const qth = thresholds[quarter] || DEFAULT_QUARTER_TH;
                  setThresholds({ Q1: { ...qth }, Q2: { ...qth }, Q3: { ...qth }, Q4: { ...qth } });
                }}
                className="text-xs text-violet-300 hover:underline"
                data-testid="comisiones-th-apply-all"
              >
                Copiar {quarter} a todos los trimestres
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="text-sm text-red-400 border border-red-900 bg-red-950/30 rounded p-3">
          Error: {error}
        </div>
      )}

      {/* Table */}
      <Card className="border-neutral-800" style={{ backgroundColor: '#111' }}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="comisiones-table">
              <thead>
                <tr className="border-b border-neutral-800 text-xs uppercase tracking-wider text-gray-400">
                  <th className="px-3 py-3 text-left">Comercial</th>
                  <th className="px-3 py-3 text-right">Facturación AR</th>
                  <th className="px-3 py-3 text-center">Cumpl. objetivo<br /><span className="text-[9px] normal-case text-gray-500">Target: {fmtARS(th.facturacion_min)}</span></th>
                  <th className="px-3 py-3 text-right">% Cobro</th>
                  <th className="px-3 py-3 text-center">Cobro ≥ {th.cobro_pct_min}%</th>
                  <th className="px-3 py-3 text-right">Roster usado<br /><span className="text-[9px] normal-case text-gray-500">Acumulado en el Q</span></th>
                  <th className="px-3 py-3 text-center">Estratégico<br /><span className="text-[9px] normal-case text-gray-500">≥ {th.roster_pct_min}%</span></th>
                  <th className="px-3 py-3 text-right">Sueldo mes 2 (base bono)</th>
                  <th className="px-3 py-3 text-center">Tier<br /><span className="text-[9px] normal-case text-gray-500">× sueldo</span></th>
                  <th className="px-3 py-3 text-right">Monto Bono</th>
                  <th className="px-3 py-3 text-center">Status</th>
                  <th className="px-3 py-3 text-center">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={12} className="text-center text-gray-500 py-8 text-sm">
                      Sin datos para {quarter} {year}. Cargá el Excel con la solapa <span className="font-mono">FACTURACION 2026</span> y <span className="font-mono">Gastos</span> (Detalle Sueldos).
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const statusText = r.multiplier === 0
                    ? 'SIN BONO'
                    : (r.cumpleCobro ? 'A PAGAR' : 'PENDIENTE COBRO');
                  const statusColor = r.multiplier === 0
                    ? '#6b7280'
                    : (r.cumpleCobro ? '#1B6B3A' : '#F4A261');
                  return (
                    <tr key={r.kam} className="border-b border-neutral-900 hover:bg-neutral-950/50" data-testid={`comisiones-row-${r.kam}`}>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-white">{r.kam}</span>
                          {r.num_canjes > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                              style={{ backgroundColor: '#7c3aed22', color: '#c4b5fd', border: '1px solid #7c3aed44' }}
                              title={`${r.num_canjes} campañas CANJE en ${quarter}${r.unique_canje_influencers ? ` · ${r.unique_canje_influencers} talento${r.unique_canje_influencers === 1 ? '' : 's'} único${r.unique_canje_influencers === 1 ? '' : 's'} de canje` : ''}`}
                              data-testid={`comisiones-canje-badge-${r.kam}`}
                            >
                              <Gift size={9} />
                              {r.num_canjes} canje{r.num_canjes === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        {r.sueldoFullName && <div className="text-[10px] text-gray-500">{r.sueldoFullName}</div>}
                        {!r.sueldoFullName && <div className="text-[10px] text-red-400">Sin sueldo cargado en Gastos</div>}
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-gray-200">{fmtARS(r.facturacion_bruto)}</td>
                      <td className="px-3 py-3 text-center font-mono">
                        <span style={{ color: r.cumplPct >= 100 ? '#4ADE80' : (r.cumplPct >= 85 ? '#F4A261' : '#E63946') }}>
                          {r.cumplPct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right font-mono text-gray-200">{r.cobro_pct.toFixed(1)}%</td>
                      <td className="px-3 py-3 text-center"><Check ok={r.cumpleCobro} /></td>
                      <td className="px-3 py-3 text-right font-mono text-gray-200">
                        <div className="text-right">
                          <span className="text-white font-semibold">{r.roster_pct.toFixed(1)}%</span>
                          <span className="text-[10px] text-gray-500 block">
                            acumulado en el Q
                          </span>
                          {r.roster_pct_monthly && (
                            <span className="text-[10px] text-gray-500 block font-normal">
                              {Object.entries(r.roster_pct_monthly).map(([mk, mv], i, arr) => (
                                <span key={mk}>
                                  {mk.slice(5)}: {mv.used_count}/{mv.roster_total} ({mv.pct.toFixed(0)}%){i < arr.length - 1 ? ' · ' : ''}
                                </span>
                              ))}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center"><Check ok={r.cumpleEstrat} /></td>
                      <td className="px-3 py-3 text-right font-mono text-gray-200">{r.sueldo_ultimo_mes > 0 ? fmtARS(r.sueldo_ultimo_mes) : <span className="text-gray-600">—</span>}</td>
                      <td className="px-3 py-3 text-center">
                        <div className="inline-flex flex-col items-center">
                          <span className="font-bold text-sm" style={{
                            color: r.multiplier >= 1.25 ? '#4ADE80' : (r.multiplier >= 1 ? '#c4b5fd' : (r.multiplier > 0 ? '#F4A261' : '#6b7280')),
                          }}>
                            {r.tierLabel}
                          </span>
                          <span className="text-[10px] text-gray-500">
                            × {r.multiplier.toFixed(2)}
                            {r.appliedPenalty && r.multiplier > 0 && <span className="text-red-400 ml-1" title="Penalidad por no cumplir objetivo estratégico">(-10%)</span>}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono font-bold text-white">{fmtARS(r.bonoMonto)}</td>
                      <td className="px-3 py-3 text-center">
                        <span
                          className="inline-block px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider"
                          style={{ backgroundColor: statusColor + '22', color: statusColor, border: `1px solid ${statusColor}` }}
                          data-testid={`comisiones-status-${r.kam}`}
                        >
                          {statusText}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setDetailFor({
                            // Todo el row + thresholds + status, para que el PDF
                            // institucional pueda armar la ficha de cumplimiento.
                            ...r,
                            invoices: r.invoices || [],
                            thresholds: th,
                            statusText: r.multiplier === 0 ? 'SIN BONO' : (r.cumpleCobro ? 'A PAGAR' : 'PENDIENTE COBRO'),
                          })}
                          disabled={!r.invoices || r.invoices.length === 0}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-300 hover:text-violet-100 hover:underline disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`comisiones-detail-${r.kam}`}
                        >
                          <Eye size={13} /> Ver ({r.invoices?.length || 0})
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info footer */}
      {data && (
        <div className="text-[11px] text-gray-500 space-y-1">
          <p>
            <strong className="text-gray-400">Fuente:</strong> Facturación AR se toma de la solapa
            <span className="font-mono"> FACTURACION 2026</span>. Sueldos de <span className="font-mono">Gastos → Detalle Sueldos</span>.
            El roster considera sólo talentos <strong>públicos</strong>.
          </p>
          {data.unmatched_salary_names?.length > 0 && (
            <p>
              <strong className="text-gray-400">Sueldos sin facturas asociadas:</strong> {data.unmatched_salary_names.join(', ')}
            </p>
          )}
        </div>
      )}
      {/* Modal: detalle de facturas por comercial × trimestre */}
      <Dialog open={!!detailFor} onOpenChange={(o) => { if (!o) { setDetailFor(null); setDetailSearch(''); } }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="comisiones-detail-modal">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <DialogTitle className="text-2xl" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
                {(detailFor?.sueldoFullName || detailFor?.kam || '')} · {QUARTER_FULL_MONTHS[quarter]} · {year}
              </DialogTitle>
              {detailFor && detailFor.invoices.length > 0 && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    onClick={() => exportDetailToExcel(detailFor)}
                    size="sm"
                    variant="outline"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700 h-8"
                    data-testid="comisiones-detail-export-excel"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
                    Excel
                  </Button>
                  <Button
                    onClick={() => exportDetailToPDF(detailFor)}
                    size="sm"
                    variant="outline"
                    className="bg-red-600 hover:bg-red-700 text-white border-red-700 h-8"
                    data-testid="comisiones-detail-export-pdf"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" />
                    PDF
                  </Button>
                </div>
              )}
            </div>
            {detailFor && (() => {
              const totalFacturas = detailFor.invoices.length;
              const facturasCobradas = detailFor.invoices.filter((i) => i.cobrado).length;
              return (
                <div className="text-xs text-gray-400 flex flex-wrap gap-4 mt-2 items-center">
                  <span>Cantidad total de facturas: <strong className="text-white">{totalFacturas}</strong></span>
                  <span>Facturas cobradas: <strong className="text-white">{facturasCobradas}</strong></span>
                  {detailFor.num_excluidas_cobro > 0 && (
                    <span data-testid="comisiones-detail-excluded-count">Excluidas del % cobro (NC + anuladas): <strong className="text-white">{detailFor.num_excluidas_cobro}</strong></span>
                  )}
                  <span>
                    % Cobrado: <strong className={detailFor.cumpleCobro ? 'text-emerald-400' : 'text-red-400'}>{detailFor.cobro_pct.toFixed(1)}%</strong>
                  </span>
                  <div className="ml-auto relative">
                    <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                    <input
                      type="text"
                      value={detailSearch}
                      onChange={(e) => setDetailSearch(e.target.value)}
                      placeholder="Buscar Nº factura, marca, influencer..."
                      className="pl-7 pr-3 py-1.5 text-xs bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500 w-64"
                      data-testid="comisiones-detail-search"
                    />
                  </div>
                </div>
              );
            })()}
          </DialogHeader>
          {detailFor && (() => {
            const q = detailSearch.trim().toLowerCase();
            const filtered = !q
              ? detailFor.invoices
              : detailFor.invoices.filter((inv) => (
                  String(inv.nro_factura || '').toLowerCase().includes(q) ||
                  String(inv.marca || '').toLowerCase().includes(q) ||
                  String(inv.agencia || '').toLowerCase().includes(q) ||
                  String(inv.influencer || '').toLowerCase().includes(q)
                ));
            const totalAttrib = filtered.reduce((s, i) => s + (i.attributed || 0), 0);
            const totalCobrado = filtered.reduce((s, i) => s + (i.cobrado_attributed || 0), 0);
            return (
            <div className="overflow-auto flex-1">
              {q && (
                <div className="px-3 py-1.5 text-[11px] text-gray-400 bg-gray-800/50 border-b border-gray-700">
                  Mostrando <strong className="text-white">{filtered.length}</strong> de <strong className="text-white">{detailFor.invoices.length}</strong> facturas · Filtro: "{detailSearch}"
                </div>
              )}
              <table className="w-full text-xs">
                <thead className="sticky top-0" style={{ backgroundColor: '#111' }}>
                  <tr className="border-b border-neutral-800 text-[10px] uppercase tracking-wider text-gray-400">
                    <th className="px-2 py-2 text-left">Fecha</th>
                    <th className="px-2 py-2 text-left">Nº Fact</th>
                    <th className="px-2 py-2 text-left">Comercial (raw)</th>
                    <th className="px-2 py-2 text-left">Marca</th>
                    <th className="px-2 py-2 text-left">Agencia</th>
                    <th className="px-2 py-2 text-left">Influencer</th>
                    <th className="px-2 py-2 text-right">Neto factura</th>
                    <th className="px-2 py-2 text-center">Dur.</th>
                    <th className="px-2 py-2 text-center">Split</th>
                    <th className="px-2 py-2 text-right">Atribuido {quarter}</th>
                    <th className="px-2 py-2 text-center">Cobrado</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={11} className="px-2 py-6 text-center text-gray-500 text-xs">
                        Sin facturas que coincidan con "{detailSearch}".
                      </td>
                    </tr>
                  )}
                  {[...filtered]
                    .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''))
                    .map((inv, idx) => (
                    <tr key={idx} className="border-b border-neutral-900 hover:bg-neutral-950/50" data-testid={`comisiones-invoice-row-${idx}`}>
                      <td className="px-2 py-2 font-mono text-gray-300">
                        {(inv.fecha || '').slice(0, 10)}
                        {inv.mes_atribucion && inv.mes_atribucion !== (inv.fecha || '').slice(0, 7) && (
                          <span className="text-[9px] text-amber-400 block" data-testid={`comisiones-invoice-mes-pedido-${idx}`}>Mes pedido: {inv.mes_atribucion}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 font-mono text-cyan-300">{inv.nro_factura || '—'}</td>
                      <td className="px-2 py-2 text-gray-400">{inv.kam_raw}</td>
                      <td className="px-2 py-2 text-white">{inv.marca || '—'}</td>
                      <td className="px-2 py-2 text-gray-400">{inv.agencia || '—'}</td>
                      <td className="px-2 py-2 text-gray-300">{inv.influencer || '—'}</td>
                      <td className="px-2 py-2 text-right font-mono text-gray-300">{fmtARS(inv.bruto_total)}</td>
                      <td className="px-2 py-2 text-center text-gray-400">{inv.duracion}m</td>
                      <td className="px-2 py-2 text-center text-gray-400">
                        {(inv.split_weight * 100).toFixed(0)}%
                        {inv.meses_en_quarter > 1 && <span className="text-[9px] text-violet-400 block">×{inv.meses_en_quarter}m en {quarter}</span>}
                      </td>
                      <td className="px-2 py-2 text-right font-mono font-bold text-white">{fmtARS(inv.attributed)}</td>
                      <td className="px-2 py-2 text-center">
                        {inv.excluido_cobro ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-neutral-700 text-gray-200" title="Excluida del % de cobro (Nota de Crédito / factura anulada)" data-testid={`comisiones-invoice-excluded-${idx}`}>
                            {(inv.tipo_doc || '').startsWith('NC') ? 'NC · excl.' : 'Anulada · excl.'}
                          </span>
                        ) : inv.cobrado ? (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ backgroundColor: '#1B6B3A', color: 'white' }}>
                            SÍ
                          </span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ backgroundColor: '#7f1d1d', color: 'white' }}>
                            NO
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-neutral-700 font-bold" style={{ backgroundColor: '#0f0f0f' }}>
                    <td colSpan={9} className="px-2 py-3 text-right text-gray-300 uppercase tracking-wider text-[10px]">
                      Totales{q ? ' (filtrados)' : ''}
                    </td>
                    <td className="px-2 py-3 text-right font-mono text-white">
                      {fmtARS(totalAttrib)}
                    </td>
                    <td className="px-2 py-3 text-center font-mono text-emerald-400">
                      {fmtARS(totalCobrado)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComisionesView;
